'use client';
import { cn } from '@/app/lib/utils';
import Image from 'next/image';
import React from 'react';

interface AvatarProps {
  name: string;
  src?: string;
  size?: number;
  imageSize?: number;
  className?: string;
  initialsClassName?: string;
  imageClassName?: string;
  isExternalUrl?: boolean;
  onClick?: () => void;
}

function getInitials(name: string) {
  if (!name || name.trim() === '') return '-';

  const trimmedName = name.trim();
  const parts = trimmedName.split(' ').filter(part => part.length > 0);

  if (parts.length === 0) return '-';

  if (parts.length === 1) {
    if (trimmedName.includes('@')) {
      const local = trimmedName.split('@')[0];
      return (local.slice(0, 2) || '-').toUpperCase();
    }
    return parts[0][0]?.toUpperCase() || '-';
  }

  const firstInitial = parts[0][0]?.toUpperCase() || '';
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || '';
  return firstInitial + lastInitial || '-';
}

const colorMap = [
  { bg: 'bg-blue-200', text: 'text-blue-950' },
  { bg: 'bg-yellow-200', text: 'text-yellow-950' },
  { bg: 'bg-secondary', text: 'text-foreground' },
  { bg: 'bg-warning/60', text: 'text-error-dark' },
  { bg: 'bg-feature-light', text: 'text-feature-dark' },
];

function getColorIndex(str: string) {
  if (!str || str.trim() === '') return 0;

  const initials = getInitials(str);
  if (initials === '-') return 0;

  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % colorMap.length;
}

const Avatar = React.memo(
  ({
    name,
    src,
    size = 40,
    imageSize,
    isExternalUrl,
    className,
    initialsClassName,
    imageClassName,
    onClick,
  }: AvatarProps) => {
    const [imageError, setImageError] = React.useState(false);
    const isValidUrl = (url: string) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };

    if (src && isValidUrl(src) && !imageError) {
      if (isExternalUrl) {
        return (
          <div
            className={cn(
              'rounded-full shrink-0 flex items-center justify-center text-base font-medium',
              onClick && 'pointer-events-auto cursor-pointer',
              className
            )}
            style={{ width: size, height: size }}
            onClick={onClick}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={name}
              className={cn('rounded-full object-contain shrink-0', imageClassName)}
              style={{ width: imageSize || size, height: imageSize || size }}
              onError={() => {
                setImageError(true);
              }}
            />
          </div>
        );
      }

      return (
        <div
          className={cn(
            'rounded-full shrink-0 flex items-center justify-center text-base font-medium',
            onClick && 'cursor-pointer',
            className
          )}
          style={{ width: size, height: size }}
          onClick={onClick}
        >
          <Image
            src={src}
            alt={name}
            className={cn('rounded-full object-contain bg-secondary shrink-0', imageClassName)}
            style={{ width: imageSize || size, height: imageSize || size }}
            width={size}
            unoptimized
            height={size}
            referrerPolicy="no-referrer"
            onError={() => {
              setImageError(true);
            }}
          />
        </div>
      );
    }

    const colorIdx = getColorIndex(name || '');
    const color = colorMap[colorIdx] || colorMap[0];
    return (
      <div
        className={cn(
          `rounded-full shrink-0 flex items-center justify-center text-base font-medium shrink-0`,
          onClick && 'cursor-pointer',
          className
        )}
        style={{ width: size, height: size }}
        onClick={onClick}
      >
        <span
          className={cn(
            `flex items-center justify-center rounded-full shrink-0 ${color.bg} ${color.text}`,
            initialsClassName
          )}
          style={{ width: imageSize || size, height: imageSize || size }}
        >
          {getInitials(name)}
        </span>
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';
export default Avatar;
